if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          mainSliderButtons: this.querySelector('.doyo-main-media-slider-buttons'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        if (this.elements.mainSliderButtons) {
          this.enableMainSliderLooping();
          this.elements.viewer.addEventListener('click', this.handleMainSliderButtonClick.bind(this), true);
          this.elements.viewer.addEventListener('mouseenter', this.showDesktopSliderButtons.bind(this));
          this.elements.viewer.addEventListener('mouseleave', this.hideDesktopSliderButtons.bind(this));
        }
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (this.elements.mainSliderButtons) {
          this.elements.viewer.addEventListener('click', this.showMobileSliderButtons.bind(this));
        }
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', () => {
            this.hideMobileSliderButtons();
            this.setActiveMedia(mediaToSwitch.dataset.target, false);
          });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      enableMainSliderLooping() {
        this.elements.viewer.enableSliderLooping = true;
        this.elements.mainSliderButtons.querySelectorAll('.slider-button').forEach((button) => {
          button.removeAttribute('disabled');
        });
      }

      handleMainSliderButtonClick(event) {
        const button = event.target.closest('.doyo-main-media-slider-buttons .slider-button');
        if (!button) return;

        this.elements.mainSliderButtons.querySelectorAll('.slider-button').forEach((sliderButton) => {
          sliderButton.classList.toggle('is-active', sliderButton === button);
        });

        const slider = this.elements.viewer.slider;
        const sliderItems = this.elements.viewer.sliderItemsToShow;
        const sliderItemOffset = this.elements.viewer.sliderItemOffset;
        const slidesPerPage = this.elements.viewer.slidesPerPage;
        const totalPages = this.elements.viewer.totalPages;
        if (!slider || !sliderItems || !sliderItemOffset || !slidesPerPage || !totalPages) return;

        event.preventDefault();
        event.stopPropagation();

        const step = Number(button.dataset.step || 1);
        const lastPosition = (sliderItems.length - slidesPerPage) * sliderItemOffset;
        const isPrevious = button.name === 'previous';
        const atStart = slider.scrollLeft <= 1;
        const atEnd = slider.scrollLeft >= lastPosition - 1;
        const nextPosition = isPrevious
          ? atStart
            ? lastPosition
            : slider.scrollLeft - step * sliderItemOffset
          : atEnd
            ? 0
            : slider.scrollLeft + step * sliderItemOffset;

        slider.scrollTo({ left: nextPosition });
      }

      showMobileSliderButtons(event) {
        if (this.mql.matches || event.target.closest('.doyo-main-media-slider-buttons')) return;
        this.elements.viewer.classList.add('doyo-mobile-controls-visible');
      }

      hideMobileSliderButtons() {
        if (!this.mql.matches) this.elements.viewer.classList.remove('doyo-mobile-controls-visible');
      }

      showDesktopSliderButtons() {
        if (this.mql.matches) this.elements.viewer.classList.add('doyo-desktop-controls-visible');
      }

      hideDesktopSliderButtons() {
        if (this.mql.matches) this.elements.viewer.classList.remove('doyo-desktop-controls-visible');
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
